/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { formatPrice } from "@lemmonhq/orders";
import type {
  PaymentDetails,
  PaymentOrderItemLine,
  RawPayment,
  TableStatus,
} from "@lemmonhq/types";
import { api } from "@app/data/api";
import { useAuth } from "@app/vue/providers/Auth";
import { useShop } from "@app/vue/providers/ShopConnection";

interface ReopenTableModalProps {
  onClose: () => void;
  payment: RawPayment;
  onSubmit: (paymentId: number) => Promise<void>;
}

const formatQuantity = (quantity: number | string) => {
  const value = Number(quantity);
  if (!Number.isFinite(value)) return String(quantity);
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

export const ReopenTableModal = defineComponent<ReopenTableModalProps>({
  name: "ReopenTableModal",
  props: ["onClose", "onSubmit", "payment"],
  setup(props) {
    const auth = useAuth();
    const { tables } = useShop();
    const { t } = useLocalization();
    const loading = ref(false);
    const detailsLoading = ref(true);
    const detailsError = ref<string>();
    const paymentDetails = ref<PaymentDetails>();

    const tableStatus = computed(() => {
      if (!props.payment.table_id) return undefined;
      return tables.value.find((table) => table.id === props.payment.table_id);
    });

    const tableName = computed(() => {
      return (
        tableStatus.value?.table.name ||
        paymentDetails.value?.table?.name ||
        t("reports.tx.reopen_table.unknown_table")
      );
    });

    const isTableBusy = computed(() => {
      const currentTable = tableStatus.value as
        | (TableStatus & { is_busy?: boolean })
        | undefined;
      return Boolean(
        currentTable?.table.is_busy ||
        currentTable?.is_busy ||
        (currentTable?.orders?.length ?? 0) > 0,
      );
    });

    const affectedItemLines = computed<PaymentOrderItemLine[]>(() => {
      if (!paymentDetails.value?.partial_payment_id) return [];
      return (paymentDetails.value.payment_order_item_lines || []).filter(
        (line) => line.line_type === "partial_item",
      );
    });

    const orderIds = computed(() => {
      const payment = paymentDetails.value || props.payment;
      return (
        payment.table_orders ||
        payment.root_table_orders_ids ||
        (payment.order_id ? [payment.order_id] : [])
      );
    });

    onMounted(async () => {
      try {
        detailsLoading.value = true;
        paymentDetails.value = await api.getPayment(props.payment.id);
      } catch (error) {
        console.error("Failed to load payment details", error);
        detailsError.value = t("reports.tx.reopen_table.load_failed");
      } finally {
        detailsLoading.value = false;
      }
    });

    const handleSubmit = async () => {
      try {
        loading.value = true;
        await props.onSubmit(props.payment.id);
      } finally {
        loading.value = false;
      }
    };

    return () => (
      <LemmonModal
        title={t("reports.tx.reopen_table.title")}
        onClose={props.onClose}
      >
        <div class="p-6 space-y-4">
          <div role="alert" class="alert alert-warning bg-warning/50">
            <span class="material-symbols-outlined">warning</span>
            <div>
              <div class="font-semibold text-accent">
                {t("reports.tx.reopen_table.warning_title")}
              </div>
              <div class="text-sm opacity-80">
                {t("reports.tx.reopen_table.warning_body")}
              </div>
            </div>
          </div>

          <div class="stats stats-vertical sm:stats-horizontal w-full border border-base-300">
            <div class="stat">
              <div class="stat-title text-accent">
                {t("reports.tx.reopen_table.table")}
              </div>
              <div class="stat-value flex items-center gap-2 text-lg">
                {tableName.value}
                <span
                  class={`badge badge-sm ${
                    isTableBusy.value ? "badge-error" : "badge-success"
                  }`}
                >
                  {isTableBusy.value
                    ? t("reports.tx.reopen_table.busy")
                    : t("reports.tx.reopen_table.free")}
                </span>
              </div>
            </div>

            <div class="stat">
              <div class="stat-title text-accent">
                {t("reports.tx.reopen_table.payment")}
              </div>
              <div class="stat-value text-lg">
                {formatPrice(
                  props.payment.amount + (props.payment.tips || 0),
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </div>
              <div class="stat-desc capitalize">
                {props.payment.method} · {props.payment.status}
              </div>
            </div>

            <div class="stat">
              <div class="stat-title text-accent">
                {t("reports.tx.reopen_table.type")}
              </div>
              <div class="stat-value text-lg">
                {paymentDetails.value?.partial_payment_id
                  ? t("reports.tx.reopen_table.partial_payment")
                  : paymentDetails.value?.split_payment_id
                    ? t("reports.tx.reopen_table.split_payment")
                    : t("reports.tx.reopen_table.full_payment")}
              </div>
              <div class="stat-desc font-mono">
                #{props.payment.id}
                {orderIds.value.length
                  ? ` · ${t("reports.tx.reopen_table.orders")} ${orderIds.value.join(", ")}`
                  : ""}
              </div>
            </div>
          </div>

          {detailsLoading.value ? (
            <div class="flex items-center justify-center rounded-box border border-base-300 p-6">
              <span class="loading loading-spinner loading-md"></span>
            </div>
          ) : detailsError.value ? (
            <div role="alert" class="alert alert-error bg-error/50">
              <span class="material-symbols-outlined">error</span>
              <span>{detailsError.value}</span>
            </div>
          ) : paymentDetails.value?.partial_payment_id ? (
            <div class="card card-border border-base-300">
              <div class="card-body gap-1 p-4">
                <h3 class="card-title text-base text-accent">
                  {t("reports.tx.reopen_table.items_title")}
                </h3>
                <p class="text-sm text-base-content/70">
                  {t("reports.tx.reopen_table.items_body")}
                </p>

                {affectedItemLines.value.length ? (
                  <div class="mt-2 divide-y divide-base-300">
                    {affectedItemLines.value.map((line) => (
                      <div
                        key={line.id}
                        class="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
                      >
                        <div class="min-w-0">
                          <div class="truncate font-medium">
                            {line.product_name ||
                              `#${line.order_item_id.toString()}`}
                          </div>
                          <div class="text-xs text-base-content/60">
                            {line.order_display_id
                              ? t(
                                  "reports.tx.reopen_table.order_number",
                                  [line.order_display_id],
                                )
                              : t(
                                  "reports.tx.reopen_table.order_item_id",
                                  [line.order_item_id],
                                )}
                          </div>
                        </div>
                        <div class="text-right shrink-0">
                          <div class="font-mono text-sm">
                            {formatQuantity(line.quantity)} ×{" "}
                            {formatPrice(
                              Number(line.unit_price),
                              true,
                              2,
                              auth.user.value?.restaurant.currency || "CHF",
                            )}
                          </div>
                          <div class="text-xs text-base-content/60">
                            {formatPrice(
                              Number(line.amount),
                              true,
                              2,
                              auth.user.value?.restaurant.currency || "CHF",
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div class="mt-2 rounded-box bg-base-200 p-3 text-sm text-base-content/70">
                    {t("reports.tx.reopen_table.no_items")}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div class="rounded-box border border-base-300 p-4 text-sm text-base-content/70">
              {t("reports.tx.reopen_table.full_payment_body")}
            </div>
          )}

          {isTableBusy.value ? (
            <div role="alert" class="alert alert-error bg-error/50">
              <span class="material-symbols-outlined">error</span>
              <span class="text-sm">
                {t("reports.tx.reopen_table.busy_warning")}
              </span>
            </div>
          ) : null}

          <div class="flex justify-end gap-2 pt-2">
            <button
              class="btn btn-ghost"
              onClick={props.onClose}
              disabled={loading.value}
            >
              {t("general.words.cancel")}
            </button>
            <button
              class="btn btn-error"
              onClick={handleSubmit}
              disabled={
                loading.value || detailsLoading.value || !!detailsError.value
              }
            >
              {loading.value ? (
                <>
                  <span class="loading loading-spinner loading-sm"></span>
                  {t("reports.tx.reopen_table.reopening")}
                </>
              ) : (
                <>
                  <span class="material-symbols-outlined">
                    table_restaurant
                  </span>
                  {t("reports.tx.reopen_table.confirm")}
                </>
              )}
            </button>
          </div>
        </div>
      </LemmonModal>
    );
  },
});
